import { ShieldCheck, Sparkles, Link2 } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HERO } from "@/data/content";
import { MessengerRow, PhoneLink } from "./ContactButtons";
import { SofiAvatar } from "../SofiAvatar";

const consult = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

const SOFI_INTRO =
  "Привіт! Мене звати Софі 👋 Я допоможу знайти найкраще авто в усій Європі, " +
  "розрахую реальну ціну на українських номерах і чесно попереджу про ризики. " +
  "А якщо захочеш — мій хазяїн Петро особисто перевірить і привезе твоє авто.";

export const Hero = () => (
  <section id="top" className="relative flex min-h-[92vh] items-center overflow-hidden">
    <div className="absolute inset-0">
      <img src={HERO.image} alt="" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60" />
    </div>

    <div className="container relative z-10 mx-auto px-4 pt-32 sm:px-6 lg:px-8 lg:pt-40">
      <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
        {/* left: message */}
        <div className="max-w-2xl">
          <div className="mb-6 flex flex-wrap gap-2" data-testid="hero-badges">
            {HERO.badges.map((b) => (
              <span key={b}
                className="rounded-full border border-[#B12A2A]/40 bg-[#B12A2A]/10 px-3 py-1 text-xs font-semibold tracking-wider text-[#E08A85]">
                {b}
              </span>
            ))}
          </div>

          <h1 className="font-heading text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Твій особистий <span className="text-[#B12A2A]">AI-радник</span>
            <br />
            з купівлі авто в Європі.
          </h1>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
            Не калькулятор, а Софі — знайде авто, чесно перевірить його та порахує реальну ціну
            на українських номерах. Один контакт від пошуку до українських номерів.
          </p>

          <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
            <Link to="/sofi?mode=find" data-testid="hero-find-button"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#B12A2A] px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-[#C73D3A]">
              <Sparkles className="h-5 w-5" /> 🚗 Найди mi auto
            </Link>
            <Link to="/sofi?mode=url" data-testid="hero-url-button"
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-[#B12A2A]/60 bg-[#B12A2A]/10 px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-[#B12A2A]/25">
              <Link2 className="h-5 w-5 text-[#B12A2A]" /> 🔗 Вставити посилання
            </Link>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button onClick={consult} data-testid="hero-consultation-button"
              className="text-sm font-medium text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline">
              Безкоштовна консультація
            </button>
            <span className="hidden text-white/20 sm:inline">·</span>
            <PhoneLink className="text-base text-white/80" />
          </div>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
            <MessengerRow />
            <span className="inline-flex items-center gap-2 text-sm text-white/50">
              <ShieldCheck className="h-4 w-4 text-[#B12A2A]" />
              Чесно. Прозоро. Під ключ.
            </span>
          </div>
        </div>

        {/* right: Sofi mascot + speech bubble */}
        <motion.div
          className="relative mx-auto hidden max-w-sm lg:block"
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="hero-sofi">
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-2xl shadow-2xl">
            <div className="flex items-center gap-4">
              <SofiAvatar size={92} state="idle" ringWidth={2} testid="hero-sofi-avatar" />
              <div>
                <div className="flex items-center gap-1.5 text-[#E08A85]">
                  <Sparkles className="h-4 w-4" />
                  <span className="text-xs font-semibold tracking-widest">СОФІ · AI</span>
                </div>
                <div className="font-heading text-2xl font-bold text-white">Мене звати Софі</div>
              </div>
            </div>
            <motion.div
              className="relative mt-5 rounded-2xl rounded-tl-sm border border-[#C73D3A]/25 bg-[#C73D3A]/[0.08] p-5"
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              data-testid="hero-sofi-bubble">
              <p className="text-[15px] leading-relaxed text-white/85">{SOFI_INTRO}</p>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* mobile Sofi bubble */}
      <motion.div
        className="mt-8 rounded-2xl border border-[#C73D3A]/25 bg-[#C73D3A]/[0.08] p-5 backdrop-blur lg:hidden"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
        data-testid="hero-sofi-mobile">
        <div className="flex gap-3">
          <SofiAvatar size={52} state="idle" ringWidth={1} className="shrink-0" testid="hero-sofi-avatar-mobile" />
          <p className="text-sm leading-relaxed text-white/85">{SOFI_INTRO}</p>
        </div>
      </motion.div>
    </div>
  </section>
);
